import { HealingOutlined } from "@mui/icons-material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import HomeIcon from "@mui/icons-material/Home";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import { Box, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import MessageDialog from "../../common/dialog/commonDialogBox";
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { styles } from "../../commonStyle/formStyles";
import {
  addAdmission,
  resetCurrentAdmission,
} from "../../redux/admissions/admissionsActions";
import { fetchBlocks } from "../../redux/block/blockAction";
import { fetchDepartments } from "../../redux/department/departmentAction";
import { fetchFloors } from "../../redux/floor/floorAction";
import {
  fetchAllPatients,
  fetchPatient,
} from "../../redux/patient/patientAction";
import { fetchWards, fetchWeekId } from "../../redux/ward/wardAction";

const AdmissionDetails = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [attenderType, setAttenderType] = useState("");
  const [numberOfAttenders, setNumberOfAttenders] = useState(1);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    message: "",
    type: "success",
  });

  // Get previously stored values if they exist
  const storedBlock = localStorage.getItem("block") || "";
  const storedFloor = localStorage.getItem("floor") || "";
  const storedWard = localStorage.getItem("ward") || "";
  
  // Format current date for default value
  const getCurrentFormattedDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      patient_id: "",
      block_id: "",
      floor_id: "",
      ward_id: "",
      department_id: "",
      attender_type: "1",
      no_of_attenders: "1",
      attender_name: "",
      attender_relation: "",
      attender_address: "",
      attender_phone: "",
      attender_name_2: "",
      attender_relation_2: "",
      attender_address_2: "",
      attender_phone_2: "",
      admit_date: getCurrentFormattedDate(),
    },
  });

  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorList = useSelector((state) => state?.floor?.fetchFloors || {});
  const departmentList = useSelector(
    (state) => state?.department?.fetchDepartments || {}
  );
  const wardList = useSelector((state) => state?.ward?.fetchWards || {});
  const patientList = useSelector(
    (state) => state?.patients?.fetchAllListPatients || {}
  );
  const admissionState = useSelector(
    (state) => state?.admission?.addAdmission || {}
  );

  const watchPatientId = watch("patient_id");
  const attenderTypes = watch("attender_type");
  const watchAttenderType = watch("attender_type");
  const watchNoOfAttenders = watch("no_of_attenders");
  const watchBlock = watch("block_id");
  const watchFloor = watch("floor_id");

  // Set current date on component mount
  useEffect(() => {
    setValue("admit_date", getCurrentFormattedDate());
  }, [setValue]);

  const getDepartmentOptions = () => {
    return departmentList.departments
      ? departmentList.departments
          .filter((department) => department.status !== 2)
          .map((department) => ({
            value: department.id,
            label: department.department_name,
          }))
      : [];
  };

  const getBlockOptions = () => {
    return (
      blockList?.Blocks?.data
        ?.filter((block) => block.block_status !== 2)
        .map((block) => ({
          value: block.id,
          label: block.block_name,
        })) || []
    );
  };

  const getFloorOptions = () => {
    return (
      floorList?.floors?.data
        ?.filter((floor) => floor.floor_status !== 0)
        .map((floor) => ({
          value: floor.id,
          label: floor.floor_name,
        })) || []
    );
  };

  const getWardOptions = () => {
    if (wardList?.wards?.data) {
      return wardList.wards.data
        .filter((ward) => ward.ward_status !== 0)
        .map((ward) => ({
          value: ward.id,
          label: ward.ward_name,
        }));
    }
    return [];
  };

  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const customDayId = dayOfWeek === 0 ? 1 : dayOfWeek + 1;

    dispatch(fetchWeekId(customDayId));
  }, [dispatch]);

  useEffect(() => {
    if (admissionState.success) {
      if (attenderTypes === "1") {
        navigate(`/admission/view/${watchPatientId}`);
      } else if (attenderTypes === "2") {
        navigate(APP_LINK.LISTADMISSIONDETAILS);
      }
      dispatch(resetCurrentAdmission());
    } else if (admissionState.error) {
      setDialogProps({
        title: "Error",
        message: "Failed to submit Admission request. Please try again.",
        type: "error",
      });
      setDialogOpen(true);
      dispatch(resetCurrentAdmission());
    }
  }, [admissionState, dispatch, navigate, attenderTypes, watchPatientId]);

  useEffect(() => {
    if (watchAttenderType) {
      setAttenderType(watchAttenderType);
    }
  }, [watchAttenderType]);

  useEffect(() => {
    if (watchNoOfAttenders) {
      const numAttenders = parseInt(watchNoOfAttenders, 10);
      if (!isNaN(numAttenders) && (numAttenders === 1 || numAttenders === 2)) {
        setNumberOfAttenders(numAttenders);
      }
    }
  }, [watchNoOfAttenders]);

  useEffect(() => {
    if (watchBlock) {
      localStorage.setItem("block", watchBlock);
    }
    if (watchFloor) {
      localStorage.setItem("floor", watchFloor);
    }
  }, [watchBlock, watchFloor]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchAllPatients());
      await dispatch(fetchDepartments());
      await dispatch(fetchBlocks());
      if (storedBlock) {
        dispatch(fetchFloors(storedBlock));

        if (storedFloor) {
          dispatch(fetchWards(storedBlock, storedFloor));
        }
      }
    };
    fetchData();
  }, [dispatch, storedBlock, storedFloor]);

  useEffect(() => {
    if (watchBlock) {
      dispatch(fetchFloors(watchBlock));
      if (watchBlock !== storedBlock) {
        setValue("floor_id", "");
        setValue("ward_id", "");
      }
    }
  }, [watchBlock, dispatch, setValue, storedBlock]);

  useEffect(() => {
    if (watchBlock && watchFloor) {
      dispatch(fetchWards(watchBlock, watchFloor));
      if (watchFloor !== storedFloor) {
        setValue("ward_id", "");
      }
    }
  }, [watchBlock, watchFloor, dispatch, setValue, storedFloor]);

  useEffect(() => {
    if (watchPatientId) {
      dispatch(fetchPatient(watchPatientId));
    }
  }, [watchPatientId, dispatch]);

  const handleCancel = () => {
    reset();
    localStorage.removeItem("block");
    localStorage.removeItem("floor");
    localStorage.removeItem("ward");
  };

  const onSubmit = (data) => {
    const selectedDate = new Date(data.admit_date);
    const formattedDate = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${selectedDate.getDate().toString().padStart(2, "0")} ${new Date()
      .getHours()
      .toString()
      .padStart(2, "0")}:${new Date().getMinutes().toString().padStart(2, "0")}`;
      
    const formdata = {
      patient_id: data.patient_id,
      block_id: data.block_id,
      floor_id: data.floor_id,
      ward_id: data.ward_id,
      department_id: data.department_id || "",
      admit_date: formattedDate,
      attender_type: data.attender_type,
    };

    const attenderDetails = [];
    if (data.attender_type === "1") {
      attenderDetails.push({
        attender_name: data.attender_name,
        relation: data.attender_relation,
        address: data.attender_address,
        phone: data.attender_phone,
      });

      if (data.no_of_attenders === "2") {
        attenderDetails.push({
          attender_name: data.attender_name_2,
          relation: data.attender_relation_2,
          address: data.attender_address_2,
          phone: data.attender_phone_2,
        });
      }
      formdata.number_attenders = data.no_of_attenders;
      formdata.attender_details = attenderDetails;
    } else {
      formdata.number_attenders = "0";
      formdata.attender_details = [];
    }

    if (!formdata.block_id || !formdata.floor_id || !formdata.ward_id) {
      setDialogProps({
        title: "Error",
        message: "Please select Block, Floor and Ward",
        type: "error",
      });
      setDialogOpen(true);
      return;
    }

    dispatch(addAdmission(formdata));
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);

    if (dialogProps.type === "success") {
      reset();
      localStorage.removeItem("block");
      localStorage.removeItem("floor");
      localStorage.removeItem("ward");
    }
    navigate(APP_LINK.LISTADMISSIONDETAILS);
  };

  const patientFields = [
    {
      name: "patient_id",
      label: "Select Patient / நோயாளியை தேர்ந்தெடுக்கவும்",
      type: "select",
      tabIndex: 1,
      options:
        patientList?.data?.data
          ?.filter((patient) => patient.admission_status !== 1)
          ?.map((patient) => ({
            value: patient.id,
            label: `${patient.patient_name} - ${patient.phone}- ${patient.ip_number}`,
          })) || [],

      rules: { required: "Patient selection is required" },
      startIcon: <PersonIcon />,
    },
    {
      name: "admit_date",
      label: "Admission Date / சேர்க்கை தேதி",
      type: "date",
      tabIndex: 2,
      rules: { required: "Admission Date is required" },
      startIcon: <PersonIcon />,
      defaultValue: getCurrentFormattedDate(),
    },
  ];

  const locationFields = [
    {
      name: "block_id",
      label: "Block / கட்டிடம்",
      placeholder: "Select block",
      type: "select",
      tabIndex: 3,
      options: getBlockOptions(),
      rules: { required: "Block is required" },
      startIcon: <LocalHospitalIcon />,
    },
    {
      name: "ward_id",
      label: "Ward Name / வார்டு பெயர்",
      placeholder: "Select ward name",
      type: "select",
      tabIndex: 5,
      options: getWardOptions(),
      rules: { required: "Ward name is required" },
      startIcon: <MeetingRoomIcon />,
    },
  ];

  const wardDeptFields = [
    {
      name: "floor_id",
      label: "Floor / மாடி",
      placeholder: "Select Floor",
      type: "select",
      tabIndex: 4,
      options: getFloorOptions(),
      rules: { required: "Floor is required" },
      startIcon: <ApartmentIcon />,
    },

    {
      name: "department_id",
      label: "Department /துறை",
      placeholder: "Select department",
      type: "select",
      tabIndex: 6,
      options: getDepartmentOptions(),
      rules: { required: "Department is required" },
      startIcon: <HealingOutlined />,
    },
  ];

  const attenderTypeField = [
    {
      name: "attender_type",
      label: "Attender Type / வருகையாளர் வகை",
      placeholder: "Select attender type",
      type: "select",
      tabIndex: 7,
      defaultValues: "1",
      options: [
        { value: "1", label: "Known Attender / தெரிந்த அட்டெண்டர்" },
        { value: "2", label: "No Attender / அட்டெண்டர் இல்லை" },
      ],
      rules: { required: "Attender type is required" },
      startIcon: <HealingOutlined />,
    },
  ];

  const attenderNumberField = [
    {
      name: "no_of_attenders",
      label: "Number of Attenders / வருகையாளர்களின் எண்ணிக்கை ",
      placeholder: "Enter number of attenders (1 or 2)",
      type: "text",
      tabIndex: 8,
      defaultValues: "1",
      rules: {
        required: "Number of attenders is required",
        pattern: {
          value: /^[1-2]$/,
          message: "Only 1 or 2 attenders are allowed",
        },
      },
      startIcon: <PersonIcon />,
    },
  ];

  const firstAttenderFields = [
    {
      name: "attender_name",
      label: "Attender Name / வருகையாளர் பெயர்",
      placeholder: "Enter attender name",
      type: "text",
      tabIndex: 9,
      rules: { required: "Attender name is required" },
      startIcon: <PersonIcon />,
    },
    {
      name: "attender_relation",
      label: "Relation with Patient / நோயாளியுடனான உறவு",
      placeholder: "Enter relation",
      type: "select",
      tabIndex: 10,
      options: [
        { value: "1", label: "Spouse / வாழ்க்கைத் துணை" },
        { value: "2", label: "Parent / பெற்றோர்" },
        { value: "3", label: "Child / குழந்தை" },
        { value: "4", label: "Sibling / உடன்பிறப்பு" },
        { value: "5", label: "Friend / நண்பர்" },
        { value: "6", label: "Other / வேறு" },
      ],
      rules: { required: "Relation is required" },
      startIcon: <FamilyRestroomIcon />,
    },
    {
      name: "attender_address",
      label: "Address / முகவரி",
      placeholder: "Enter address",
      type: "text",
      tabIndex: 11,
      multipleLine: true,
      rules: { required: "Address is required" },
      startIcon: <HomeIcon />,
    },
    {
      name: "attender_phone",
      label: "Phone Number /  தொலைபேசி எண்",
      placeholder: "Enter phone number",
      type: "text",
      tabIndex: 12,
      maxLength: 10,
      showCharCount: false,
      rules: {
        required: "Phone number is required",
        pattern: {
          value: /^(6|7|8|9)\d{9}$/,
          message: "Please enter a valid 10-digit phone number",
        },
        maxLength: {
          value: 10,
          message: "Phone number should be 10 digits",
        },
      },
      startIcon: <PhoneIcon />,
    },
  ];

  const secondAttenderFields = [
    {
      name: "attender_name_2",
      label: "Second Attender Name / இரண்டாவது வருகையாளர் பெயர்",
      placeholder: "Enter attender name",
      type: "text",
      tabIndex: 13,
      rules: { required: "Second attender name is required" },
      startIcon: <PersonIcon />,
    },
    {
      name: "attender_relation_2",
      label: "Relation with Patient / நோயாளியுடனான உறவு",
      placeholder: "Enter relation",
      type: "select",
      tabIndex: 14,
      options: [
        { value: "1", label: "Spouse/ வாழ்க்கைத் துணை" },
        { value: "2", label: "Parent / தந்தை/தாய்" },
        { value: "3", label: "Child / குழந்தை" },
        { value: "4", label: "Sibling  / உடன்பிறப்பு" },
        { value: "5", label: "Friend / நண்பன்" },
        { value: "6", label: "Other / மற்றவர்" },
      ],
      rules: { required: "Relation is required" },
      startIcon: <FamilyRestroomIcon />,
    },
    {
      name: "attender_address_2",
      label: "Address /  நோயாளியின் பெயர்",
      placeholder: "Enter address",
      type: "text",
      tabIndex: 15,
      multipleLine: true,
      rules: { required: "Address is required" },
      startIcon: <HomeIcon />,
    },
    {
      name: "attender_phone_2",
      label: "Phone Number /  தொலைபேசி எண்",
      placeholder: "Enter phone number",
      type: "text",
      tabIndex: 16,
      maxLength: 10,
      showCharCount: false,
      rules: {
        required: "Phone number is required",
        pattern: {
          value: /^(6|7|8|9)\d{9}$/,
          message: "Please enter a valid 10-digit phone number",
        },
      },
      startIcon: <PhoneIcon />,
    },
  ];

  return (
    <Box sx={styles.container}>
      <Paper sx={styles.paper}>
        <Typography variant="h4" sx={styles.heading}>
          Admission Registers
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 4 }}>
          Complete all fields to request an admission
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <ReusableFormFields
                  inputFields={patientFields}
                  register={register}
                  errors={errors}
                  setValue={setValue}
                  control={control}
                  gridProps={{ xs: 12, lg: 6 }}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Current Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ReusableFormFields
                    inputFields={locationFields}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReusableFormFields
                    inputFields={wardDeptFields}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Attender Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ReusableFormFields
                    inputFields={attenderTypeField}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    control={control}
                    gridProps={{ xs: 12 }}
                  />
                </Grid>

                {watchAttenderType === "1" && (
                  <Grid item xs={12} sm={6}>
                    <ReusableFormFields
                      inputFields={attenderNumberField}
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      control={control}
                      gridProps={{ xs: 12 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            {watchAttenderType === "1" && (
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {watchNoOfAttenders === "1" || watchNoOfAttenders === "2" ? (
                    <Grid item xs={12} sm={watchNoOfAttenders === "2" ? 6 : 12}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        First Attender Details
                      </Typography>
                      <ReusableFormFields
                        inputFields={firstAttenderFields}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        control={control}
                        gridProps={{ xs: 12 }}
                      />
                    </Grid>
                  ) : null}
                  {watchNoOfAttenders === "2" && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Second Attender Details
                      </Typography>
                      <ReusableFormFields
                        inputFields={secondAttenderFields}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        control={control}
                        gridProps={{ xs: 12 }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box sx={styles.buttonsContainer}>
                <CustomButton
                  text="Back"
                  variant="contained"
                  size="small"
                  onClick={handleCancel}
                  tabIndex={17}
                />
                <CustomButton
                  type="submit"
                  text="Submit"
                  variant="contained"
                  size="small"
                  tabIndex={18}
                />
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <MessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title={dialogProps.title}
        message={dialogProps.message}
        type={dialogProps.type}
      />
    </Box>
  );
};

export default AdmissionDetails;